import {objectFromEntries} from '@benchling/js-utils';
import produce, {original} from 'immer';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import coerceMappedSourceValueToDestinationColumn from 'notebook/structured-tables/structured-table-mappings/coerceMappedSourceValueToDestinationColumn';
import {StructuredTableMappingActionOptions} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {trackPerformStructuredTableMappingEvent} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsAnalytics';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import addRowsToTable from 'notebook/structured-tables/util/addRowsToTable';
import getAllStructuredTablesInNote from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import {Row} from 'notebook/tables/constants/RowConstants';
import {TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {findProcedureConditionReplicateColumnIndex} from 'procedures/tables/ProcedureConditionReplicateColumnUtils';

/** Given a source table, apply all the table mappings to other structured tables in the note. Returns a new
 * modified Note. */
export default function performStructuredTableMappings(
  note: EntryNote,
  fileId: string,
  sourceTableProvenanceUuid: string,
  options: StructuredTableMappingActionOptions
): {updatedNote: EntryNote; tableOp?: TableOpDiffMap} {
  const tableMappingsState = StructuredTableMappingsGlobalState.getState(fileId);
  const tableMappingsFromSource = StructuredTableMappingsStateManager.getMappingsForSourceTable(
    tableMappingsState,
    sourceTableProvenanceUuid
  );
  const sourceTable = getAllStructuredTablesInNote(note).find(
    (table) => table.provenanceUuid === sourceTableProvenanceUuid
  );

  assert(
    tableMappingsFromSource.length > 0 && sourceTable != null,
    `Table mapping performed from missing source structured table (provenanceUuid=${sourceTableProvenanceUuid}).`
  );

  const sourceColumnUuidToIndexMap = getStructuredTableColumnUuidToIndexMap(sourceTable);
  const sourceConditionReplicateColumnIndex =
    BenchlingConfig.PROCEDURES_ENABLE_CONDITION_REPLICATE_TABLE_MAPPING
      ? findProcedureConditionReplicateColumnIndex(sourceTable)
      : undefined;
  const evaluatedSourceTable = note.noteRuntimeSnapshot.evaluationSnapshot?.[sourceTable.name];
  assert(
    evaluatedSourceTable != null,
    `Table mapping performed for source structured table (provenanceUuid=${sourceTableProvenanceUuid}) with no evaluated table.`
  );

  const filteredMappingsByDestinationTableUuid = objectFromEntries(
    Object.values(tableMappingsFromSource).map((tableMapping) => [
      tableMapping.destinationTableProvenanceUuid,
      tableMapping,
    ])
  );
  // we only want to map the specified rows from the source table
  const sourceTableNewRows = evaluatedSourceTable.filter((_, index) =>
    options.sourceTableRowIndexes.includes(index)
  );
  const sessionId = analytics.makeAnalyticsEventId();

  const tableOp: TableOpDiffMap = {};

  return {
    updatedNote: produce(note, (draft) => {
      const structuredTables = getAllStructuredTablesInNote(draft);
      const destinationStructuredTables = structuredTables.filter(
        (table) => filteredMappingsByDestinationTableUuid[table.provenanceUuid]
      );
      // iterate through each of our mapped destination tables
      for (const destinationTable of destinationStructuredTables) {
        const tableMapping = filteredMappingsByDestinationTableUuid[destinationTable.provenanceUuid];
        const columnMappings = tableMapping.structuredTableColumnMappings;
        const replicationFactor = tableMapping.replicationFactor ?? 1;
        const destinationColumnUuidToIndexMap = getStructuredTableColumnUuidToIndexMap(
          original(destinationTable)! // it's more performant to iterate through the original for non-mutative actions
        );
        const destinationConditionReplicateColumnIndex =
          BenchlingConfig.PROCEDURES_ENABLE_CONDITION_REPLICATE_TABLE_MAPPING
            ? findProcedureConditionReplicateColumnIndex(original(destinationTable)!)
            : undefined;
        // create new rows to be added to the destination table
        const newDestinationRowsToAdd: Array<Row> = [];
        for (let newRowIndex = 0; newRowIndex < sourceTableNewRows.length; newRowIndex++) {
          for (let replicationRowIndex = 0; replicationRowIndex < replicationFactor; replicationRowIndex++) {
            // Create an empty row to fill. The cells here are just plain blank cells rather than created
            // with TableAdapter.getBlankRow because addRowsToTable uses getBlankRow anyway.
            const destinationRowToAdd = makeEmptyRow(destinationTable.columns.length);
            // for each mapped column, copy over the evaluated typed value from the source
            for (const columnMapping of columnMappings) {
              const sourceColumnIndex =
                sourceColumnUuidToIndexMap[columnMapping.sourceTableColumnProvenanceUuid];
              const destinationColumnIndex =
                destinationColumnUuidToIndexMap[columnMapping.destinationTableColumnProvenanceUuid];
              assert(
                sourceColumnIndex != null,
                `Invalid table mapping from column (provenanceUuid=${columnMapping.sourceTableColumnProvenanceUuid}); Column does not exist.`
              );
              assert(
                destinationColumnIndex != null,
                `Invalid table mapping to column (provenanceUuid=${columnMapping.destinationTableColumnProvenanceUuid}); Column does not exist.`
              );

              destinationRowToAdd[destinationColumnIndex].typedValue =
                coerceMappedSourceValueToDestinationColumn(
                  sourceTableNewRows[newRowIndex][sourceColumnIndex].value,
                  destinationTable.columns[destinationColumnIndex]
                );
            }
            // if both tables have a Condition-Replicate column, copy that value to the destination table
            if (
              sourceConditionReplicateColumnIndex != null &&
              destinationConditionReplicateColumnIndex != null
            ) {
              destinationRowToAdd[destinationConditionReplicateColumnIndex].typedValue =
                coerceMappedSourceValueToDestinationColumn(
                  sourceTableNewRows[newRowIndex][sourceConditionReplicateColumnIndex].value,
                  destinationTable.columns[destinationConditionReplicateColumnIndex]
                );
            }
            newDestinationRowsToAdd.push(destinationRowToAdd);
          }
        }
        if (newDestinationRowsToAdd.length) {
          const addRowsResult = addRowsToTable(
            newDestinationRowsToAdd,
            destinationTable,
            getStructuredTableAdapterFromTable(destinationTable),
            null,
            // If the table is empty (only 1 row), then we replace that first row.
            // If we're dealing with Parallel Execution / multibatch destination tables, we allow overwriting
            // many trailing rows, since those rows were auto-expanded.
            {
              removeEmptyTrailingRows:
                destinationTable.rows.length === 1 || destinationConditionReplicateColumnIndex != null,
            }
          );
          if (addRowsResult) {
            const destinationTableWithAddedRows = addRowsResult.updatedTable;
            tableOp[destinationTable.name] = addRowsResult.tableOp;
            destinationTable.rows = destinationTableWithAddedRows.rows;
            trackPerformStructuredTableMappingEvent(
              sessionId,
              options.analytics.actionMethod,
              fileId,
              tableMapping,
              sourceTable,
              original(destinationTable)!,
              {
                // if all the rows in the table were newly created, then consider it the first submit
                // (note: archiving or deleting all rows may allow for mutiple of these events, but that's okay)
                sourceFirstSubmit: sourceTable.rows.every((row, rowIndex) =>
                  options.sourceTableRowIndexes.includes(rowIndex)
                ),
                // we send all three of these numbers to know how many empty rows were replaced
                destinationNumOriginalRows: original(destinationTable)!.rows.length,
                destinationNumFinalRows: destinationTableWithAddedRows.rows.length,
                destinationNumRowsAdded: newDestinationRowsToAdd.length,
              }
            );
          } else {
            // if null, the rows could not be added because the destination table was already too large
            // (the addRowsToTable function already sends the max rows message)
            notifications.warn(
              `Table Mapping could not be performed from ${sourceTable.name} to ${destinationTable.name}`
            );
          }
        }
      }
    }),
    tableOp: Object.keys(tableOp).length > 0 ? tableOp : undefined,
  };
}

function getStructuredTableColumnUuidToIndexMap(
  structuredTable: UnknownStructuredTable
): Record<string, number | undefined> {
  return objectFromEntries(structuredTable.columns.map((column, index) => [column.provenanceUuid, index]));
}

function makeEmptyRow(numCells: number): Row {
  return Array(numCells)
    .fill(undefined)
    .map(() => TableUtil.makeEmptyCell());
}
